"use client";

import React, { useEffect, useState } from "react";
import makeApiCall from "@packages/lib/server-actions/rest-api";
import API_URL from "@packages/lib/server-actions/api-urls";
import { useAuthSession } from "@packages/hooks/useAuthSession";
import {
  clickstreamForClicks,
  gaForCtaClicks,
  getProjectSpecificData,
  isPGS,
  isWhatuni,
  oldGAForCtaClicks,
} from "@packages/lib/utlils/commonFunction";
import { ClickstreamInterface } from "@packages/lib/utlils/clickstream";
import { ClickstreamConstants } from "@packages/shared-components/services/constants";
import ButtonLoader from "./ButtonLoader";
import { getCustomDomain } from "@packages/lib/utlils/common-function-server";
import { setNonAdvertFlowCookie } from "@packages/lib/utlils/non-advert-pdf-utils";
import { triggerSigninToast } from "@packages/shared-components/common-utilities/toast/signin-toast";
import RegistrationModal from "@packages/shared-components/user-profile/signin-signup/registration-modal";
import { useOnRegistrationModalClose } from "@packages/hooks/useIsRegistrationModal";
import ExitIntentForm from "@packages/shared-components/exit-intent-form/exit-intent-form";

interface RequestInfoProps {
  enquiryProps: any;
  commonGAAttrs?: any;
  btnSize?: string;
  btnColor?: string;
  commonOldGAAttrs?: any;
}

const RequestInfo = ({
  enquiryProps,
  commonGAAttrs,
  btnSize = "w-full",
  btnColor = "btn-blue",
  commonOldGAAttrs,
}: RequestInfoProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated, authToken } = useAuthSession({
    redirectOnUnauthenticated: false,
  });
  const [url, setUrl] = useState<any>(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);

  // Show exit-intent popup for guest users once per session (external URLs only)
  // Whatuni only — exit-intent flow is not enabled for PGS.
  const triggerExitIntent = () => {
    if (
      isWhatuni() &&
      !isAuthenticated &&
      !sessionStorage.getItem("exitIntentShown")
    ) {
      sessionStorage.setItem("exitIntentShown", "true");
      setShowExitIntent(true);
    }
  };

  // Listen for modal close event dispatched by Registration/LoginForm after auth completes
  useOnRegistrationModalClose(() => setShowRegistrationModal(false));
  const csData: ClickstreamInterface = {} as ClickstreamInterface;
  csData.CTATitle = ClickstreamConstants.requestInfoCTA;
  csData.actionType = ClickstreamConstants.clickAction;
  csData.eventType = "EnquiryFormClicked";
  csData.functionalityName = (
    enquiryProps?.functionalityName ||
    (enquiryProps?.sponsoredListingFlag === "Y"
      ? ClickstreamConstants.sponsorListing
      : "requestinfo")
  ).replace(/\s+/g, ""); // Remove all spaces

  useEffect(() => {
    if (url) window.location.href = url;
  }, [url]);

  const handleExternalRequest = async (externalUrl: string, response: any) => {
    console.log("commonOldGAAttrs", commonOldGAAttrs);
    csData.eventType = ClickstreamConstants.requestInfoExternalEvent;
    csData.externalUrl = externalUrl;
    enquiryProps.url = externalUrl;
    enquiryProps.revenueValue = response?.webformPrice;
    commonGAAttrs.click_text = csData.CTATitle;
    enquiryProps.event = "email_external";
    gaForCtaClicks(
      enquiryProps,
      "request_info_external",
      commonGAAttrs,
      response.cpeFlag,
    );
    !(isWhatuni() && commonGAAttrs.page_grouping === "scholarship_pages") &&
      oldGAForCtaClicks(
        enquiryProps,
        "Request info",
        enquiryProps.url,
        enquiryProps.event,
        commonOldGAAttrs,
        response.cpeFlag,
        "",
      );
    await clickstreamForClicks(enquiryProps, csData, commonGAAttrs);
    window.open(externalUrl, "_blank");
    triggerExitIntent();
  };

  const buildRequestInfoUrl = (response: any) => {

    const contextpath = isPGS() ? "pgs" : "degrees";
    const baseUrl = `/${contextpath}/email${enquiryProps?.collegeName ? `/${enquiryProps?.collegeName}-email` : ""}${enquiryProps?.collegeId ? `/${enquiryProps?.collegeId}` : ""}${enquiryProps?.courseId ? `/${enquiryProps?.courseId}` : "/0"}${enquiryProps?.selectedSubject ? `/${enquiryProps?.selectedSubject}` : "/0"}`;

    const subOrderId = enquiryProps?.subOrderItemId
      ? `/n-${enquiryProps?.subOrderItemId}`
      : "";

    if (enquiryProps?.pageName === "browsemoneypageresults") {
      const sponsoredParam =
        enquiryProps?.sponsoredListingFlag === "Y"
          ? `sponsoredOrderItemId=${enquiryProps?.orderItemId || "0"}`
          : "sponsoredOrderItemId=0";
      return `${baseUrl}${subOrderId}/send-college-email.html?${sponsoredParam}&manualBoostingFlag=${enquiryProps?.manualBoostingFlag || "N"}&pageName=sr&fromPage=${enquiryProps?.pageName}`;
    }

    return `${baseUrl}${subOrderId}/send-college-email.html?fromPage=${enquiryProps?.pageName}`;
  };

  const getNonAdvertUrl = async () => {
    if (isAuthenticated && authToken) {

      if(isPGS()){
        const oracleUrl = `/api/get-pdf?p_college_id=${enquiryProps?.collegeId || ""}&p_course_id=${enquiryProps?.courseId || "0"}`;

        try {
          const res = await fetch(oracleUrl);
          const data = await res.json();
          if (data?.pdfUrl) {
            window.open(data.pdfUrl, "_blank");
          }
        } catch (error) {
          console.error("Error fetching PDF URL:", error);
        }

        triggerSigninToast("PDF download successful");
        return null;
      } else {
        const fromType = enquiryProps?.courseId ? "COURSE" : "PROVIDER";
        const pdfUrl = `/degrees/get-pdf?from=${fromType}&collegeId=${enquiryProps?.collegeId || ""}&courseId=${enquiryProps?.courseId || "0"}`;

        // Open PDF in new tab using anchor element - shares all cookies/session/referrer with opener
        const anchor = document.createElement("a");
        anchor.href = pdfUrl;
        anchor.target = "_blank";
        anchor.rel = "opener";
        anchor.style.display = "none";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();

        triggerSigninToast("PDF download successful");
        return null;
      }
    }

    // Guest user: store nonAdvertDetails in localStorage, set cookie, and open modal
    const nonAdvertDetails = `${enquiryProps?.collegeDisplayName || ""}##${enquiryProps?.collegeId}####${enquiryProps?.courseId || "0"}#${enquiryProps?.logoUrl || ""}#${enquiryProps?.courseId ? "COURSE" : "PROVIDER"}#${enquiryProps?.pageName || ""}`;
    localStorage.setItem("nonAdvertDetails", nonAdvertDetails);
    // return `/register/?pagetype=non-request-info${enquiryProps?.courseName ? `&coursename=${enquiryProps?.courseName}` : ""}&collegename=${enquiryProps?.collegeName}&collegeid=${enquiryProps?.collegeId}`;
    setNonAdvertFlowCookie();
    setShowRegistrationModal(true);
    return null;
  };

  const handleAdvertiserRequest = async () => {
    console.log("commonOldGAAttrs", commonOldGAAttrs);
    commonGAAttrs.study_mode = enquiryProps?.studyMode || "NA";
    if (sessionStorage.getItem("veppleProviders")) {
      commonGAAttrs.data_label5 = "vepple_driven";
    }

    const response = await makeApiCall({
      url: `${process.env.NEXT_PUBLIC_DOMSERVICE_API_DOMAIN}/${API_URL?.getEnquiryDetails}`,
      method: "POST",
      body: {
        suborderItemId: enquiryProps?.subOrderItemId,
        orderItemId: enquiryProps?.orderItemId,
        collegeId: enquiryProps?.collegeId,
        courseId: enquiryProps?.courseId,
        affiliateId: getProjectSpecificData("affiliateId"),
        qualCode: enquiryProps?.qualCode,
      },
      headers: {
        "x-api-key": `${process.env.NEXT_PUBLIC_DOMSERVICE_X_API_KEY}`,
      },
    });

    const externalUrl =
      response?.stdRequestInfoUrl || response?.requestInfoWebform;

    if (externalUrl) {
      await handleExternalRequest(externalUrl, response);
    } else if (response?.requestInfoEmail) {
      csData.eventType = ClickstreamConstants.requestInfoInternalEvent;
      const requestUrl = buildRequestInfoUrl(response);
      enquiryProps.revenueValue = response?.emailPrice;
      enquiryProps.url = getCustomDomain() + requestUrl;
      commonGAAttrs.click_text = csData.CTATitle;
      enquiryProps.event = "request info";
      gaForCtaClicks(
        enquiryProps,
        "request_info_internal",
        commonGAAttrs,
        response.cpeFlag,
      );
      !(isWhatuni() && commonGAAttrs.page_grouping === "scholarship_pages") &&
        oldGAForCtaClicks(
          enquiryProps,
          "Request info",
          enquiryProps.url,
          "email_request",
          commonOldGAAttrs,
          response.cpeFlag,
          "",
        );
      await clickstreamForClicks(enquiryProps, csData, commonGAAttrs);
      setUrl(requestUrl);
    }
  };

  const handleNonAdvertiserRequest = async () => {
    const requestUrl = await getNonAdvertUrl();

    // If null, the registration modal was opened for guest users
    if (!requestUrl) return;

    enquiryProps.url = requestUrl;
    commonGAAttrs.click_text = csData.CTATitle;
    gaForCtaClicks(enquiryProps, "request_info_internal", commonGAAttrs, "");
    csData.eventType = ClickstreamConstants.requestInfoInternalEvent;
    await clickstreamForClicks(enquiryProps, csData, commonGAAttrs);
    setUrl(requestUrl);
  };

  const handleRequestInfo = async () => {
    setIsLoading(true);
    try {
      const isAdvertiser =
        enquiryProps?.subOrderItemId ||
        enquiryProps?.sponsoredListingFlag === "Y";

      if (isAdvertiser) {
        await handleAdvertiserRequest();
      } else {
        await handleNonAdvertiserRequest();
      }
    } catch (error) {
      console.error("Error fetching enquiry data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`btn ${btnColor} flex justify-center items-center ${btnSize} ${enquiryProps?.hasRequiredFlags ? "justify-self-end lg:w-[162px]" : ""}`}
        onClick={handleRequestInfo}
      >
        <ButtonLoader isLoading={isLoading}>
          {ClickstreamConstants.requestInfoCTA}
        </ButtonLoader>
      </button>
      {showRegistrationModal && (
        <RegistrationModal onClose={() => setShowRegistrationModal(false)} />
      )}
      {showExitIntent && (
        <ExitIntentForm
          sourceClickText="request_info"
          onClose={() => setShowExitIntent(false)}
          onSubmit={() => {}}
        />
      )}
    </>
  );
};

export default RequestInfo;
